import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiCheck, FiMail, FiLock, FiArrowRight, FiBook, FiAward, FiEye, FiEyeOff, FiInfo, FiX } from 'react-icons/fi';

// Form Data Interface
interface RegisterFormData {
  studentEmail: string;
  studentPassword: string;
  studentVerified: boolean;
  studentFirstName: string;
  studentLastName: string;
  state: string;
  city: string;
  schoolName: string;
  parentEmail: string;
  parentPassword: string;
  parentVerified: boolean;
  parentFirstName: string;
  parentLastName: string;
  parentMobile: string;
  board: 'CBSE' | 'State Board' | '';
  standard: 'Grade 7' | 'Grade 8' | 'Grade 9' | '';
  medium: string;
  selectedSubjects: string[];
}

const initialFormData: RegisterFormData = {
  studentEmail: '',
  studentPassword: '',
  studentVerified: false,
  studentFirstName: '',
  studentLastName: '',
  state: '',
  city: '',
  schoolName: '',
  parentEmail: '',
  parentPassword: '',
  parentVerified: false,
  parentFirstName: '',
  parentLastName: '',
  parentMobile: '',
  board: '',
  standard: '',
  medium: '',
  selectedSubjects: [],
};

// Available Indian states for dropdown
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Step state
  // 1: Student Email Verification, 2: Student Details, 3: Parent Email & Details, 4: Board & Standard, 5: Subject Trial, 6: Welcome Congrats
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<RegisterFormData>(initialFormData);

  // Resume registration progress from login redirection
  useEffect(() => {
    const state = location.state as { resumeEmail?: string; step?: number };
    if (state?.resumeEmail) {
      setFormData(prev => ({ ...prev, studentEmail: state.resumeEmail || '' }));
      if (state.step) {
        setStep(state.step);
        toast.success('Resuming your incomplete registration!');
      }
    }
  }, [location.state]);

  // Verification code states
  const [studentCode, setStudentCode] = useState('');
  const [parentCode, setParentCode] = useState('');
  const [isVerifyingStudentEmail, setIsVerifyingStudentEmail] = useState(false);
  const [isVerifyingParentEmail, setIsVerifyingParentEmail] = useState(false);
  const [loading, setLoading] = useState(false);

  // Password visibility states
  const [showStudentPassword, setShowStudentPassword] = useState(false);
  const [showParentPassword, setShowParentPassword] = useState(false);

  // Validation errors state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dynamic standards, mediums and subjects list fetched from DB
  const [standardsList, setStandardsList] = useState<any[]>([]);
  const [mediumsList, setMediumsList] = useState<any[]>([]);
  const [subjectsList, setSubjectsList] = useState<any[]>([]);
  const [fetchingOptions, setFetchingOptions] = useState(false);

  useEffect(() => {
    const fetchMediums = async () => {
      try {
        const response = await fetch('/api/student/register/mediums');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setMediumsList(data);
          }
        }
      } catch (err) {
        console.warn('Failed to fetch mediums', err);
      }
    };
    fetchMediums();
  }, []);

  // Fetch standards when board changes
  useEffect(() => {
    if (!formData.board) return;

    const fetchStandards = async () => {
      try {
        setFetchingOptions(true);
        const response = await fetch(`/api/student/register/standards?board=${encodeURIComponent(formData.board)}`);
        if (response.ok) {
          const data = await response.json();
          setStandardsList(data);
          // If the current standard is not in the new standard list, reset it
          if (formData.standard && !data.some((s: any) => s.name === formData.standard)) {
            updateField('standard', '');
          }
        } else {
          // Fallback if API Gateway authorizer blocks the request
          if (formData.board.toUpperCase() === 'CBSE') {
            const fallbackData = [
              { id: 1, name: 'Grade 7', board: 'CBSE' },
              { id: 2, name: 'Grade 8', board: 'CBSE' },
              { id: 3, name: 'Grade 9', board: 'CBSE' }
            ];
            setStandardsList(fallbackData);
            if (formData.standard && !fallbackData.some((s: any) => s.name === formData.standard)) {
              updateField('standard', '');
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch standards:', err);
        if (formData.board.toUpperCase() === 'CBSE') {
          const fallbackData = [
            { id: 1, name: 'Grade 7', board: 'CBSE' },
            { id: 2, name: 'Grade 8', board: 'CBSE' },
            { id: 3, name: 'Grade 9', board: 'CBSE' }
          ];
          setStandardsList(fallbackData);
          if (formData.standard && !fallbackData.some((s: any) => s.name === formData.standard)) {
            updateField('standard', '');
          }
        }
      } finally {
        setFetchingOptions(false);
      }
    };

    fetchStandards();
  }, [formData.board]);

  // Fetch subjects when standard or board changes
  useEffect(() => {
    if (!formData.board || !formData.standard) return;

    // Find the standardId corresponding to standard name
    const selectedStandardObj = standardsList.find(s => s.name === formData.standard);
    const standardId = selectedStandardObj ? selectedStandardObj.id : null;
    if (!standardId) return;

    const fetchSubjects = async () => {
      try {
        setFetchingOptions(true);
        const response = await fetch(`/api/student/register/subjects?board=${encodeURIComponent(formData.board)}&standardId=${standardId}`);
        if (response.ok) {
          const data = await response.json();
          setSubjectsList(data);
          // Auto-select all available subjects by default for the trial
          const subjectCodes = data.map((sub: any) => sub.code || sub.name);
          updateField('selectedSubjects', subjectCodes);
        }
      } catch (err) {
        console.error('Failed to fetch subjects:', err);
      } finally {
        setFetchingOptions(false);
      }
    };

    fetchSubjects();
  }, [formData.board, formData.standard, standardsList]);

  const updateField = (field: keyof RegisterFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validatePassword = (password: string) => {
    if (password.length < 8) return false;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?"{}|<>_+=\-[\]\\\/]/.test(password);
    return hasUpper && hasLower && hasDigit && hasSpecial;
  };

  // Student password policy checks
  const sMeetsMinLength = formData.studentPassword.length >= 8;
  const sHasUppercase = /[A-Z]/.test(formData.studentPassword);
  const sHasLowercase = /[a-z]/.test(formData.studentPassword);
  const sHasDigit = /[0-9]/.test(formData.studentPassword);
  const sHasSpecial = /[!@#$%^&*(),.?"{}|<>_+=\-[\]\\\/]/.test(formData.studentPassword);

  // Parent password policy checks
  const pMeetsMinLength = formData.parentPassword.length >= 8;
  const pHasUppercase = /[A-Z]/.test(formData.parentPassword);
  const pHasLowercase = /[a-z]/.test(formData.parentPassword);
  const pHasDigit = /[0-9]/.test(formData.parentPassword);
  const pHasSpecial = /[!@#$%^&*(),.?"{}|<>_+=\-[\]\\\/]/.test(formData.parentPassword);

  // Resume check when student email is input
  const handleCheckEmail = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.studentEmail) {
      newErrors.studentEmail = 'Student Email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.studentEmail)) {
        newErrors.studentEmail = 'Please enter a valid email address.';
      }
    }

    if (!formData.studentPassword) {
      newErrors.studentPassword = 'Password is required.';
    } else if (!validatePassword(formData.studentPassword)) {
      newErrors.studentPassword = 'Password must meet all complexity requirements below.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setLoading(true);
      // Query database for saved state
      const response = await fetch(`/api/student/register-status?email=${encodeURIComponent(formData.studentEmail)}`);

      if (response.ok) {
        const savedState = await response.json();
        if (savedState && savedState.currentStep === 6) {
          toast.error('User already exists, please login.');
          navigate('/login');
          return;
        }
        if (savedState && savedState.formData) {
          setFormData(prev => ({
            ...prev,
            ...savedState.formData,
            selectedSubjects: savedState.formData.selectedSubjects || []
          }));
          setStep(savedState.currentStep);
          toast.success('Found your incomplete registration! Resuming where you left off.');
          return;
        }
      }

      // If no saved state, start verification flow
      const initResponse = await fetch('/api/student/register/initiate-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.studentEmail, role: 'Student' })
      });

      if (!initResponse.ok) {
        const errJson = await initResponse.json();
        throw new Error(errJson.message || 'Failed to send verification email.');
      }

      setIsVerifyingStudentEmail(true);
      toast.success('Verification code sent to student email!');
    } catch (err: any) {
      toast.error(err.message || 'Error initiating verification.');
    } finally {
      setLoading(false);
    }
  };

  // Sync state to DB on step completion
  const persistStateToDB = async (nextStep: number, updatedData: RegisterFormData) => {
    try {
      const sanitizedData = { ...updatedData };
      delete (sanitizedData as any).studentPassword;
      delete (sanitizedData as any).parentPassword;

      await fetch('/api/student/register-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: updatedData.studentEmail,
          currentStep: nextStep,
          formData: sanitizedData
        })
      });
    } catch (err) {
      console.warn('Unable to sync registration state to database:', err);
    }
  };

  const handleNextStep = (next: number, dataOverride?: Partial<RegisterFormData>) => {
    const updated = { ...formData, ...dataOverride };
    setFormData(updated);
    setStep(next);
    persistStateToDB(next, updated);
  };

  // Student Code Check
  const verifyStudentCode = async () => {
    if (!studentCode || studentCode.length !== 6) {
      setErrors(prev => ({ ...prev, studentCode: 'Please enter a valid 6-digit verification code.' }));
      return;
    }
    setErrors(prev => {
      const next = { ...prev };
      delete next.studentCode;
      return next;
    });

    try {
      setLoading(true);
      const verifyResponse = await fetch('/api/student/register/confirm-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.studentEmail,
          code: studentCode,
          password: formData.studentPassword
        })
      });

      if (!verifyResponse.ok) {
        const errJson = await verifyResponse.json();
        throw new Error(errJson.message || 'Invalid verification code.');
      }

      toast.success('Student email verified!');
      handleNextStep(2, { studentVerified: true });
      setIsVerifyingStudentEmail(false);
    } catch (err: any) {
      toast.error(err.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  // Parent Email check & trigger code
  const handleParentEmailSend = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.parentEmail) {
      newErrors.parentEmail = 'Parent Email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.parentEmail)) {
        newErrors.parentEmail = 'Please enter a valid email address.';
      } else if (formData.parentEmail.toLowerCase() === formData.studentEmail.toLowerCase()) {
        newErrors.parentEmail = 'Parent email must be different from student email.';
      }
    }

    if (!formData.parentPassword) {
      newErrors.parentPassword = 'Parent Password is required.';
    } else if (!validatePassword(formData.parentPassword)) {
      newErrors.parentPassword = 'Password must meet all complexity requirements below.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setLoading(true);
      const initResponse = await fetch('/api/student/register/initiate-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.parentEmail, role: 'Parent' })
      });

      if (!initResponse.ok) {
        const errJson = await initResponse.json();
        throw new Error(errJson.message || 'Failed to send parent verification email.');
      }

      setIsVerifyingParentEmail(true);
      toast.success('Verification code sent to parent email!');
    } catch (err: any) {
      toast.error(err.message || 'Verification initiation error.');
    } finally {
      setLoading(false);
    }
  };

  // Parent Code Check
  const verifyParentCode = async () => {
    if (!parentCode || parentCode.length !== 6) {
      setErrors(prev => ({ ...prev, parentCode: 'Please enter a valid 6-digit verification code.' }));
      return;
    }
    setErrors(prev => {
      const next = { ...prev };
      delete next.parentCode;
      return next;
    });

    try {
      setLoading(true);
      const verifyResponse = await fetch('/api/student/register/confirm-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.parentEmail,
          code: parentCode,
          password: formData.parentPassword
        })
      });

      if (!verifyResponse.ok) {
        const errJson = await verifyResponse.json();
        throw new Error(errJson.message || 'Invalid verification code.');
      }

      toast.success('Parent email verified!');
      setFormData(prev => ({ ...prev, parentVerified: true }));
      setIsVerifyingParentEmail(false);
    } catch (err: any) {
      toast.error(err.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  // Submit complete onboarding registration to Cognito & MySQL
  const handleCompleteOnboarding = async () => {
    if (formData.selectedSubjects.length === 0) {
      toast.error('Please select at least one subject.');
      return;
    }

    try {
      setLoading(true);
      const requestPayload = {
        student: {
          firstName: formData.studentFirstName,
          lastName: formData.studentLastName,
          email: formData.studentEmail,
          password: formData.studentPassword,
          state: formData.state,
          schoolName: formData.schoolName,
          phoneNumber: '',
          address: formData.city
        },
        parent: {
          firstName: formData.parentFirstName,
          lastName: formData.parentLastName,
          phoneNumber: formData.parentMobile,
          email: formData.parentEmail,
          password: formData.parentPassword,
          sameAsStudentEmail: false
        },
        academic: {
          board: formData.board,
          standard: formData.standard,
          medium: formData.medium
        },
        subjects: formData.selectedSubjects
      };

      const response = await fetch('/api/users/registration/student-onboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Onboarding failed.');
      }

      toast.success('Onboarding complete!');
      handleNextStep(6);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Verification / registration error.');
    } finally {
      setLoading(false);
    }
  };

  // Trigger confetti particle simulation on Congrats page (step 6)
  useEffect(() => {
    if (step !== 6) return;

    // Set registration completion flag in DB
    const completeRegistration = async () => {
      try {
        await fetch(`/api/student/register-status/complete?email=${encodeURIComponent(formData.studentEmail)}`, {
          method: 'POST'
        });
      } catch (err) {
        console.error('Failed to mark registration completed:', err);
      }
    };
    completeRegistration();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: any[] = [];
    const colorsList = ['#ff6b65', '#ffd166', '#06d6a0', '#118ab2', '#073b4c', '#ff7a45', '#3b82f6'];

    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        r: Math.random() * 6 + 4,
        d: Math.random() * canvas.height,
        color: colorsList[Math.floor(Math.random() * colorsList.length)],
        tilt: Math.random() * 10 - 5,
        tiltAngleIncremental: Math.random() * 0.07 + 0.02,
        tiltAngle: 0
      });
    }

    let animationId: number;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.tiltAngle += p.tiltAngleIncremental;
        p.y += (Math.cos(p.d) + 3 + p.r / 2) / 2;
        p.x += Math.sin(p.tiltAngle);
        p.tilt = Math.sin(p.tiltAngle - p.r / 2) * 12;

        ctx.beginPath();
        ctx.lineWidth = p.r;
        ctx.strokeStyle = p.color;
        ctx.moveTo(p.x + p.tilt + p.r / 2, p.y);
        ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 2);
        ctx.stroke();

        if (p.y > canvas.height) {
          p.x = Math.random() * canvas.width;
          p.y = -20;
          p.tilt = Math.random() * 10 - 5;
        }
      });
      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => cancelAnimationFrame(animationId);
  }, [step]);

  return (
    <div className="min-h-screen relative flex items-center justify-center p-6 bg-gradient-to-br from-[#fffbeb] via-[#ffe4e6] to-[#e0f2fe] overflow-hidden select-none font-sans">

      {/* Background SVG illustrations representing learning/fun */}
      <div className="absolute top-10 left-10 w-28 h-28 opacity-10 pointer-events-none text-[#ff6b35]">
        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z" /></svg>
      </div>
      <div className="absolute bottom-10 right-10 w-36 h-36 opacity-10 pointer-events-none text-[#1b4ddb]">
        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.04-.42 1.99-1.07 2.75z" /></svg>
      </div>

      {/* Main Registration Card */}
      <div className="relative w-full max-w-xl bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl border border-white/50 p-8 space-y-6 transition-all duration-300 z-10">

        {/* Progress Dots Indicator */}
        {step < 6 && (
          <div className="flex items-center justify-center space-x-2 pb-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`h-2.5 rounded-full transition-all duration-300 ${step === s || (s === 1 && step < 2) || (s === 2 && step === 2)
                    ? 'w-8 bg-[#1b4ddb]'
                    : 'w-2.5 bg-gray-200'
                  }`}
              />
            ))}
          </div>
        )}

        {/* ---------------- STEP 1: Student Email Verification ---------------- */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <span className="text-4xl block">🎒</span>
              <h2 className="text-2xl font-extrabold text-[#1a2d54]">Start Your Journey!</h2>
              <p className="text-sm text-gray-500 font-medium">Let's verify your student email and set a password.</p>
            </div>

            {!isVerifyingStudentEmail ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className={`text-xs font-bold uppercase tracking-wide ${errors.studentEmail ? 'text-red-500' : 'text-gray-400'}`}>Student Email <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <FiMail className={`absolute left-4 top-1/2 -translate-y-1/2 ${errors.studentEmail ? 'text-red-400' : 'text-gray-400'}`} size={18} />
                    <input
                      type="email"
                      value={formData.studentEmail}
                      onChange={(e) => updateField('studentEmail', e.target.value)}
                      placeholder="e.g. student@school.com"
                      className={`w-full pl-12 pr-4 py-3.5 rounded-2xl border focus:outline-none focus:ring-2 font-medium text-sm text-[#1b2559] bg-white/70 ${errors.studentEmail
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-blue-500'
                        }`}
                    />
                  </div>
                  {errors.studentEmail && (
                    <p className="text-red-500 text-xs font-bold pl-1 animate-fadeIn">{errors.studentEmail}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className={`text-xs font-bold uppercase tracking-wide ${errors.studentPassword ? 'text-red-500' : 'text-gray-400'}`}>Choose Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <FiLock className={`absolute left-4 top-1/2 -translate-y-1/2 ${errors.studentPassword ? 'text-red-400' : 'text-gray-400'}`} size={18} />
                    <input
                      type={showStudentPassword ? "text" : "password"}
                      value={formData.studentPassword}
                      onChange={(e) => updateField('studentPassword', e.target.value)}
                      placeholder="Minimum 8 characters"
                      className={`w-full pl-12 pr-12 py-3.5 rounded-2xl border focus:outline-none focus:ring-2 font-medium text-sm text-[#1b2559] bg-white/70 ${errors.studentPassword
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-blue-500'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowStudentPassword(!showStudentPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showStudentPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {errors.studentPassword && (
                    <p className="text-red-500 text-xs font-bold pl-1 animate-fadeIn">{errors.studentPassword}</p>
                  )}
                  {formData.studentPassword && (
                    <div className="p-3.5 rounded-2xl bg-gray-50/80 space-y-1.5 border border-gray-100 text-xs text-left animate-fadeIn mt-2">
                      <div className="font-bold flex items-center text-gray-600 mb-1">
                        <FiInfo className="mr-1" /> Password Requirements:
                      </div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-gray-500 font-medium">
                        <div className="flex items-center space-x-1">
                          {sMeetsMinLength ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                          <span>Min 8 characters</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          {sHasUppercase ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                          <span>Uppercase letter</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          {sHasLowercase ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                          <span>Lowercase letter</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          {sHasDigit ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                          <span>At least one number</span>
                        </div>
                        <div className="flex items-center space-x-1 col-span-2">
                          {sHasSpecial ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                          <span>Special character (!@#$%^&*...)</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleCheckEmail}
                  disabled={loading}
                  className="w-full py-4 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition flex items-center justify-center space-x-2 text-sm shadow-md"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <>
                      <span>Next</span>
                      <FiArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className={`text-xs font-bold uppercase tracking-wide ${errors.studentCode ? 'text-red-500' : 'text-gray-400'}`}>Enter 6-Digit Code <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    maxLength={6}
                    value={studentCode}
                    onChange={(e) => {
                      setStudentCode(e.target.value);
                      if (errors.studentCode) {
                        setErrors(prev => {
                          const next = { ...prev };
                          delete next.studentCode;
                          return next;
                        });
                      }
                    }}
                    placeholder="Enter code"
                    className={`w-full text-center tracking-widest text-lg font-bold py-3 rounded-2xl border focus:outline-none focus:ring-2 text-[#1b2559] bg-white/70 ${errors.studentCode
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:ring-blue-500'
                      }`}
                  />
                  {errors.studentCode && (
                    <p className="text-red-500 text-xs font-bold pl-1 text-center animate-fadeIn">{errors.studentCode}</p>
                  )}
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={handleCheckEmail}
                      disabled={loading}
                      className="text-xs font-bold text-blue-600 hover:text-blue-700 transition hover:underline disabled:opacity-50"
                    >
                      Resend Code
                    </button>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setIsVerifyingStudentEmail(false);
                      setErrors({});
                    }}
                    disabled={loading}
                    className="w-1/3 py-3.5 rounded-2xl border border-gray-200 hover:bg-gray-50 transition text-gray-600 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Back
                  </button>
                  <button
                    onClick={verifyStudentCode}
                    disabled={loading}
                    className="flex-1 py-3.5 rounded-2xl text-white font-bold bg-emerald-500 hover:bg-emerald-600 transition text-sm flex items-center justify-center space-x-1 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <>
                        <FiCheck size={16} />
                        <span>Verify</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------------- STEP 2: Student Personal Details ---------------- */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <span className="text-4xl block">✏️</span>
              <h2 className="text-2xl font-extrabold text-[#1a2d54]">Tell Us About Yourself</h2>
              <p className="text-sm text-gray-500 font-medium">Please enter your basic school details.</p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className={`text-xs font-bold ${errors.studentFirstName ? 'text-red-500' : 'text-gray-400'}`}>First Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.studentFirstName}
                    onChange={(e) => updateField('studentFirstName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 ${errors.studentFirstName
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:ring-blue-500'
                      }`}
                  />
                  {errors.studentFirstName && (
                    <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.studentFirstName}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className={`text-xs font-bold ${errors.studentLastName ? 'text-red-500' : 'text-gray-400'}`}>Last Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.studentLastName}
                    onChange={(e) => updateField('studentLastName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 ${errors.studentLastName
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:ring-blue-500'
                      }`}
                  />
                  {errors.studentLastName && (
                    <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.studentLastName}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <label className={`text-xs font-bold ${errors.state ? 'text-red-500' : 'text-gray-400'}`}>State <span className="text-red-500">*</span></label>
                <select
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 bg-white ${errors.state
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:ring-blue-500'
                    }`}
                >
                  <option value="">Select State</option>
                  {STATES.map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.state}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className={`text-xs font-bold ${errors.city ? 'text-red-500' : 'text-gray-400'}`}>City <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 ${errors.city
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:ring-blue-500'
                      }`}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.city}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className={`text-xs font-bold ${errors.schoolName ? 'text-red-500' : 'text-gray-400'}`}>School Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.schoolName}
                    onChange={(e) => updateField('schoolName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 ${errors.schoolName
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:ring-blue-500'
                      }`}
                  />
                  {errors.schoolName && (
                    <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.schoolName}</p>
                  )}
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => {
                    setStep(1);
                    setErrors({});
                  }}
                  className="w-1/3 py-3.5 rounded-2xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    const newErrors: Record<string, string> = {};
                    if (!formData.studentFirstName) newErrors.studentFirstName = 'First Name is required.';
                    if (!formData.studentLastName) newErrors.studentLastName = 'Last Name is required.';
                    if (!formData.state) newErrors.state = 'State is required.';
                    if (!formData.city) newErrors.city = 'City is required.';
                    if (!formData.schoolName) newErrors.schoolName = 'School Name is required.';

                    if (Object.keys(newErrors).length > 0) {
                      setErrors(newErrors);
                      return;
                    }
                    setErrors({});
                    handleNextStep(3);
                  }}
                  className="flex-1 py-3.5 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition text-sm flex items-center justify-center space-x-1 shadow-md"
                >
                  <span>Next</span>
                  <FiArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- STEP 3: Parent Registration ---------------- */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <span className="text-4xl block">👪</span>
              <h2 className="text-2xl font-extrabold text-[#1a2d54]">Parent/Guardian Details</h2>
              <p className="text-sm text-gray-500 font-medium">Verify your parent's email and set a password.</p>
            </div>

            {!formData.parentVerified ? (
              <div className="space-y-4">
                {!isVerifyingParentEmail ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className={`text-xs font-bold uppercase tracking-wide ${errors.parentEmail ? 'text-red-500' : 'text-gray-400'}`}>Parent Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        value={formData.parentEmail}
                        onChange={(e) => updateField('parentEmail', e.target.value)}
                        placeholder="e.g. parent@guardian.com"
                        className={`w-full px-4 py-3.5 rounded-2xl border focus:outline-none focus:ring-2 text-sm text-[#1b2559] ${errors.parentEmail
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-200 focus:ring-blue-500'
                          }`}
                      />
                      {errors.parentEmail && (
                        <p className="text-red-500 text-xs font-bold pl-1 animate-fadeIn">{errors.parentEmail}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className={`text-xs font-bold uppercase tracking-wide ${errors.parentPassword ? 'text-red-500' : 'text-gray-400'}`}>Parent Password <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <FiLock className={`absolute left-4 top-1/2 -translate-y-1/2 ${errors.parentPassword ? 'text-red-400' : 'text-gray-400'}`} size={18} />
                        <input
                          type={showParentPassword ? "text" : "password"}
                          value={formData.parentPassword}
                          onChange={(e) => updateField('parentPassword', e.target.value)}
                          placeholder="Minimum 8 characters"
                          className={`w-full pl-12 pr-12 py-3.5 rounded-2xl border focus:outline-none focus:ring-2 text-sm text-[#1b2559] bg-white/70 ${errors.parentPassword
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-200 focus:ring-blue-500'
                            }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowParentPassword(!showParentPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                        >
                          {showParentPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                        </button>
                      </div>
                      {errors.parentPassword && (
                        <p className="text-red-500 text-xs font-bold pl-1 animate-fadeIn">{errors.parentPassword}</p>
                      )}
                      {formData.parentPassword && (
                        <div className="p-3.5 rounded-2xl bg-gray-50/80 space-y-1.5 border border-gray-100 text-xs text-left animate-fadeIn mt-2">
                          <div className="font-bold flex items-center text-gray-600 mb-1">
                            <FiInfo className="mr-1" /> Password Requirements:
                          </div>
                          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-gray-500 font-medium">
                            <div className="flex items-center space-x-1">
                              {pMeetsMinLength ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                              <span>Min 8 characters</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              {pHasUppercase ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                              <span>Uppercase letter</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              {pHasLowercase ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                              <span>Lowercase letter</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              {pHasDigit ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                              <span>At least one number</span>
                            </div>
                            <div className="flex items-center space-x-1 col-span-2">
                              {pHasSpecial ? <FiCheck className="text-emerald-500" /> : <FiX className="text-red-500" />}
                              <span>Special character (!@#$%^&*...)</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setStep(2);
                          setErrors({});
                        }}
                        disabled={loading}
                        className="w-1/3 py-3.5 rounded-2xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleParentEmailSend}
                        disabled={loading}
                        className="flex-1 py-3.5 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition text-sm flex items-center justify-center space-x-1 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                          <span>Send Code</span>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-xs text-amber-500 font-semibold text-center bg-amber-50 py-2 rounded-xl">
                      Sent verification code to parent email.
                    </p>
                    <div className="space-y-2">
                      <label className={`text-xs font-bold uppercase tracking-wide ${errors.parentCode ? 'text-red-500' : 'text-gray-400'}`}>Code <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        maxLength={6}
                        value={parentCode}
                        onChange={(e) => {
                          setParentCode(e.target.value);
                          if (errors.parentCode) {
                            setErrors(prev => {
                              const next = { ...prev };
                              delete next.parentCode;
                              return next;
                            });
                          }
                        }}
                        placeholder="Enter 6-digit code"
                        className={`w-full text-center tracking-widest text-lg font-bold py-3 rounded-2xl border focus:outline-none focus:ring-2 text-[#1b2559] ${errors.parentCode
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-200 focus:ring-blue-500'
                          }`}
                      />
                      {errors.parentCode && (
                        <p className="text-red-500 text-xs font-bold pl-1 text-center animate-fadeIn">{errors.parentCode}</p>
                      )}
                      <div className="text-right">
                        <button
                          type="button"
                          onClick={handleParentEmailSend}
                          disabled={loading}
                          className="text-xs font-bold text-blue-600 hover:text-blue-700 transition hover:underline disabled:opacity-50"
                        >
                          Resend Code
                        </button>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setIsVerifyingParentEmail(false);
                          setErrors({});
                        }}
                        disabled={loading}
                        className="w-1/3 py-3.5 rounded-2xl border border-gray-200 text-gray-600 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Back
                      </button>
                      <button
                        onClick={verifyParentCode}
                        disabled={loading}
                        className="flex-1 py-3.5 rounded-2xl text-white font-bold bg-emerald-500 hover:bg-emerald-600 transition text-sm flex items-center justify-center space-x-1 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                          <>
                            <FiCheck size={16} />
                            <span>Verify</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 animate-fadeIn">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className={`text-xs font-bold ${errors.parentFirstName ? 'text-red-500' : 'text-gray-400'}`}>First Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.parentFirstName}
                      onChange={(e) => updateField('parentFirstName', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 ${errors.parentFirstName
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-blue-500'
                        }`}
                    />
                    {errors.parentFirstName && (
                      <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.parentFirstName}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className={`text-xs font-bold ${errors.parentLastName ? 'text-red-500' : 'text-gray-400'}`}>Last Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.parentLastName}
                      onChange={(e) => updateField('parentLastName', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 ${errors.parentLastName
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-blue-500'
                        }`}
                    />
                    {errors.parentLastName && (
                      <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.parentLastName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className={`text-xs font-bold ${errors.parentMobile ? 'text-red-500' : 'text-gray-400'}`}>Mobile Phone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    value={formData.parentMobile}
                    onChange={(e) => updateField('parentMobile', e.target.value)}
                    placeholder="e.g. +91 98765 43210"
                    className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 ${errors.parentMobile
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:ring-blue-500'
                      }`}
                  />
                  {errors.parentMobile && (
                    <p className="text-red-500 text-[10px] font-bold pl-1 animate-fadeIn">{errors.parentMobile}</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={() => {
                      setFormData(prev => ({ ...prev, parentVerified: false }));
                      setErrors({});
                    }}
                    className="w-1/3 py-3.5 rounded-2xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition"
                  >
                    Change Email
                  </button>
                  <button
                    onClick={() => {
                      const newErrors: Record<string, string> = {};
                      if (!formData.parentFirstName) newErrors.parentFirstName = 'First Name is required.';
                      if (!formData.parentLastName) newErrors.parentLastName = 'Last Name is required.';
                      if (!formData.parentMobile) newErrors.parentMobile = 'Mobile Phone is required.';

                      if (Object.keys(newErrors).length > 0) {
                        setErrors(newErrors);
                        return;
                      }
                      setErrors({});
                      handleNextStep(4);
                    }}
                    className="flex-1 py-3.5 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition text-sm flex items-center justify-center space-x-1 shadow-md"
                  >
                    <span>Next</span>
                    <FiArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------------- STEP 4: Board & Standard Selection ---------------- */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <span className="text-4xl block">🏫</span>
              <h2 className="text-2xl font-extrabold text-[#1a2d54]">Board & Standard</h2>
              <p className="text-sm text-gray-500 font-medium">Choose your active school curriculum.</p>
            </div>

            <div className="space-y-6">
              {/* Board Selection */}
              <div className="space-y-3">
                <label className={`text-xs font-bold uppercase tracking-wider ${errors.board ? 'text-red-500' : 'text-gray-400'}`}>Select Board <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-4">
                  {['CBSE', 'State Board'].map((b) => (
                    <button
                      key={b}
                      onClick={() => updateField('board', b as any)}
                      className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${formData.board === b
                          ? 'border-[#1b4ddb] bg-blue-50/50 text-[#1b4ddb]'
                          : errors.board
                            ? 'border-red-300 hover:border-red-400 text-red-500 bg-red-50/20'
                            : 'border-gray-100 hover:border-gray-200 text-gray-500'
                        }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
                {errors.board && (
                  <p className="text-red-500 text-xs font-bold pl-1 animate-fadeIn">{errors.board}</p>
                )}
              </div>

              {/* Standard Selection */}
              <div className="space-y-3">
                <label className={`text-xs font-bold uppercase tracking-wider ${errors.standard ? 'text-red-500' : 'text-gray-400'}`}>Select Standard <span className="text-red-500">*</span></label>
                {!formData.board ? (
                  <p className="text-xs font-medium text-gray-400 italic">Please select a board first</p>
                ) : fetchingOptions && standardsList.length === 0 ? (
                  <div className="flex items-center space-x-2 text-xs font-medium text-gray-500">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                    <span>Loading standards...</span>
                  </div>
                ) : standardsList.length === 0 ? (
                  <p className="text-xs text-amber-500 italic">No standards available for this board</p>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {standardsList.map((std) => (
                      <button
                        key={std.id}
                        onClick={() => updateField('standard', std.name)}
                        className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${formData.standard === std.name
                            ? 'border-[#1b4ddb] bg-blue-50/50 text-[#1b4ddb]'
                            : errors.standard
                              ? 'border-red-300 hover:border-red-400 text-red-500 bg-red-50/20'
                              : 'border-gray-100 hover:border-gray-200 text-gray-500'
                          }`}
                      >
                        {std.name}
                      </button>
                    ))}
                  </div>
                )}
                {errors.standard && (
                  <p className="text-red-500 text-xs font-bold pl-1 animate-fadeIn">{errors.standard}</p>
                )}
              </div>

              {/* Medium Selection */}
              <div className="space-y-3">
                <label className={`text-xs font-bold uppercase tracking-wider ${errors.medium ? 'text-red-500' : 'text-gray-400'}`}>Select Medium <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-4">
                  {(mediumsList.length > 0 ? mediumsList : [{ id: 1, mediumName: 'English' }, { id: 2, mediumName: 'Hindi' }, { id: 3, mediumName: 'Gujarati' }]).map((m) => {
                    const name = m.mediumName || m.name || m;
                    return (
                      <button
                        key={m.id || name}
                        type="button"
                        onClick={() => updateField('medium', name)}
                        className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${formData.medium === name
                            ? 'border-[#1b4ddb] bg-blue-50/50 text-[#1b4ddb]'
                            : errors.medium
                              ? 'border-red-300 hover:border-red-400 text-red-500 bg-red-50/20'
                              : 'border-gray-100 hover:border-gray-200 text-gray-500'
                          }`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
                {errors.medium && (
                  <p className="text-red-500 text-xs font-bold pl-1 animate-fadeIn">{errors.medium}</p>
                )}
              </div>

              <div className="flex space-x-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setStep(3);
                    setErrors({});
                  }}
                  className="w-1/3 py-3.5 rounded-2xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    const newErrors: Record<string, string> = {};
                    if (!formData.board) newErrors.board = 'Board selection is required.';
                    if (!formData.standard) newErrors.standard = 'Standard selection is required.';
                    if (!formData.medium) newErrors.medium = 'Medium selection is required.';

                    if (Object.keys(newErrors).length > 0) {
                      setErrors(newErrors);
                      return;
                    }
                    setErrors({});
                    handleNextStep(5);
                  }}
                  className="flex-1 py-3.5 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition text-sm flex items-center justify-center space-x-1 shadow-md"
                >
                  <span>Next</span>
                  <FiArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- STEP 5: Subject Selection ---------------- */}
        {step === 5 && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <span className="text-4xl block">📚</span>
              <h2 className="text-2xl font-extrabold text-[#1a2d54]">Your Trial Subjects</h2>
              <p className="text-sm text-gray-500 font-medium">All subjects are activated for your free 7-day trial!</p>
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold text-blue-600 bg-blue-50 p-3 rounded-2xl leading-relaxed text-center">
                ✨ **Trial Bonus**: You get access to the first 2 full chapters of every selected subject for 7 days!
              </p>

              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {fetchingOptions && subjectsList.length === 0 ? (
                  <div className="flex items-center justify-center space-x-2 text-xs font-medium text-gray-500 py-6">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                    <span>Loading subjects...</span>
                  </div>
                ) : subjectsList.length === 0 ? (
                  <p className="text-sm text-gray-400 italic text-center py-6">No subjects found for this selection.</p>
                ) : (
                  subjectsList.map((sub) => {
                    const code = sub.code || sub.name;
                    const isChecked = (formData.selectedSubjects || []).includes(code);
                    return (
                      <div
                        key={sub.id || code}
                        className="p-3.5 rounded-2xl border-2 border-blue-200 bg-blue-50/10 flex items-center justify-between cursor-not-allowed opacity-80"
                      >
                        <span className="text-sm font-bold text-gray-700 flex items-center space-x-2">
                          <FiBook className="text-[#1b4ddb]" />
                          <span>{sub.name}</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={true}
                          className="rounded border-gray-300 text-[#1b4ddb] focus:ring-0 w-4 h-4 cursor-not-allowed"
                        />
                      </div>
                    );
                  })
                )}
              </div>
              {errors.selectedSubjects && (
                <p className="text-red-500 text-xs font-bold pl-1 text-center animate-fadeIn">{errors.selectedSubjects}</p>
              )}

              <div className="flex space-x-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setStep(4);
                    setErrors({});
                  }}
                  className="w-1/3 py-3.5 rounded-2xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (formData.selectedSubjects.length === 0) {
                      setErrors({ selectedSubjects: 'Please select at least one subject.' });
                      return;
                    }
                    setErrors({});
                    handleCompleteOnboarding();
                  }}
                  disabled={loading}
                  className="flex-1 py-3.5 rounded-2xl text-white font-bold bg-emerald-500 hover:bg-emerald-600 transition text-sm flex items-center justify-center space-x-1 shadow-md"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <>
                      <span>Activate Trial</span>
                      <FiCheck size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- STEP 6: Congrats Welcome Screen ---------------- */}
        {step === 6 && (
          <div className="text-center space-y-6 py-6 z-20 relative">
            <span className="text-6xl block animate-bounce">🎉</span>
            <div className="space-y-2">
              <h2 className="text-3xl font-black text-[#1a2d54] tracking-tight">Congratulations!</h2>
              <p className="text-base text-gray-500 font-bold">
                Welcome to Masti Manch, {formData.studentFirstName}!
              </p>
              <p className="text-xs text-gray-400 font-medium max-w-sm mx-auto leading-relaxed pt-1">
                Your 7-day trial is now active. You have access to 2 chapters of all your selected subjects.
              </p>
            </div>

            <div className="bg-[#f0fdf4] border border-emerald-100 rounded-3xl p-4 max-w-md mx-auto space-y-2">
              <div className="flex justify-center space-x-2 text-emerald-600 text-sm font-bold items-center">
                <FiAward size={18} />
                <span>7-Day Trial Activated</span>
              </div>
              <p className="text-[10px] text-emerald-600 font-medium">
                Grade: {formData.standard} • Board: {formData.board} • Medium: {formData.medium}
              </p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="px-8 py-3.5 bg-[#1b4ddb] hover:bg-blue-700 text-white font-black rounded-2xl shadow-xl transition transform hover:scale-105 active:scale-95 text-sm"
            >
              Sign In to Your Dashboard
            </button>
          </div>
        )}

        {/* Footer Link back to login */}
        {step < 6 && (
          <div className="pt-4 border-t border-gray-150 text-center text-xs font-semibold text-gray-500">
            <span>Already have an account? </span>
            <Link to="/login" className="text-[#1b4ddb] hover:underline font-bold ml-1">
              Log in instead
            </Link>
          </div>
        )}

      </div>

      {/* Confetti canvas overlay on congrats page */}
      {step === 6 && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none z-0"
        />
      )}
    </div>
  );
};

export default RegisterPage;
